import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { menuAPI } from './menuAPI';
import {
  MenuItem,
  MenuCategory,
  CreateMenuItemDto,
  UpdateMenuItemDto,
  CreateMenuCategoryDto,
  UpdateMenuCategoryDto,
  MenuQueryDto,
  ImportMenuResult,
} from './types/menu.types';

interface MenuState {
  items: MenuItem[];
  categories: MenuCategory[];
  selectedItem: MenuItem | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  importResult: ImportMenuResult | null;
}

const initialState: MenuState = {
  items: [],
  categories: [],
  selectedItem: null,
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  loading: false,
  error: null,
  importResult: null,
};

// ==================== ASYNC THUNKS - MENU ITEMS ====================

export const fetchMenuItems = createAsyncThunk(
  'menu/fetchItems',
  async (query: MenuQueryDto) => {
    const response = await menuAPI.getMenuItems(query);
    return response;
  },
);

export const fetchMenuItem = createAsyncThunk(
  'menu/fetchItem',
  async (uuid: string) => {
    const response = await menuAPI.getMenuItem(uuid);
    return response;
  },
);

export const createMenuItem = createAsyncThunk(
  'menu/createItem',
  async (data: CreateMenuItemDto | FormData, { rejectWithValue }) => {
    try {
      const response = await menuAPI.createMenuItem(data);
      return response;
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        'Failed to create menu item';
      return rejectWithValue({ message });
    }
  },
);

export const updateMenuItem = createAsyncThunk(
  'menu/updateItem',
  async ({ uuid, data }: { uuid: string; data: UpdateMenuItemDto | FormData }, { rejectWithValue }) => {
    try {
      const response = await menuAPI.updateMenuItem(uuid, data);
      return response;
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        'Failed to update menu item';
      return rejectWithValue({ message });
    }
  },
);

export const deleteMenuItem = createAsyncThunk(
  'menu/deleteItem',
  async (uuid: string) => {
    await menuAPI.deleteMenuItem(uuid);
    return uuid;
  },
);

export const importMenuItems = createAsyncThunk(
  'menu/importItems',
  async ({ file, updateIfExists }: { file: File; updateIfExists: boolean }) => {
    const response = await menuAPI.importMenuItems(file, updateIfExists);
    return response;
  },
);

// ==================== ASYNC THUNKS - CATEGORIES ====================

export const fetchCategories = createAsyncThunk(
  'menu/fetchCategories',
  async () => {
    const response = await menuAPI.getCategories();
    return response;
  },
);

export const createCategory = createAsyncThunk(
  'menu/createCategory',
  async (data: CreateMenuCategoryDto, { rejectWithValue }) => {
    try {
      const response = await menuAPI.createCategory(data);
      return response;
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        'Failed to create category';
      return rejectWithValue({ message });
    }
  },
);

export const updateCategory = createAsyncThunk(
  'menu/updateCategory',
  async ({ uuid, data }: { uuid: string; data: UpdateMenuCategoryDto }, { rejectWithValue }) => {
    try {
      const response = await menuAPI.updateCategory(uuid, data);
      return response;
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        'Failed to update category';
      return rejectWithValue({ message });
    }
  },
);

export const deleteCategory = createAsyncThunk(
  'menu/deleteCategory',
  async (uuid: string) => {
    await menuAPI.deleteCategory(uuid);
    return uuid;
  },
);

// ==================== SLICE ====================

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearImportResult: (state) => {
      state.importResult = null;
    },
    clearSelectedItem: (state) => {
      state.selectedItem = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ==================== MENU ITEMS ====================
    
    builder
      // Fetch menu items
      .addCase(fetchMenuItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenuItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = Array.isArray(action.payload.items)
          ? action.payload.items
          : [];
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchMenuItems.rejected, (state, action) => {
        state.loading = false;
        state.items = [];
        state.error = action.error.message || 'Failed to fetch menu items';
      })

      // Fetch single menu item
      .addCase(fetchMenuItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenuItem.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedItem = action.payload;
      })
      .addCase(fetchMenuItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch menu item';
      })

      // Create menu item
      .addCase(createMenuItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMenuItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
      })
      .addCase(createMenuItem.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to create menu item';
      })

      // Update menu item
      .addCase(updateMenuItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMenuItem.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(
          (item) => item.uuid === action.payload.uuid,
        );
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.selectedItem?.uuid === action.payload.uuid) {
          state.selectedItem = action.payload;
        }
      })
      .addCase(updateMenuItem.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to update menu item';
      })

      // Delete menu item
      .addCase(deleteMenuItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMenuItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.uuid !== action.payload);
      })
      .addCase(deleteMenuItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete menu item';
      })

      // Import menu items
      .addCase(importMenuItems.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.importResult = null;
      })
      .addCase(importMenuItems.fulfilled, (state, action) => {
        state.loading = false;
        state.importResult = action.payload;
      })
      .addCase(importMenuItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to import menu items';
      })

    // ==================== CATEGORIES ====================

      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.categories = [];
        state.error = action.error.message || 'Failed to fetch categories';
      })

      // Create category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories.push(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to create category';
      })

      // Update category
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categories.findIndex(
          (cat) => cat.uuid === action.payload.uuid,
        );
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to update category';
      })

      // Delete category
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = state.categories.filter(
          (cat) => cat.uuid !== action.payload,
        );
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete category';
      });
  },
});

export const {
  clearError,
  clearImportResult,
  clearSelectedItem,
  setPage,
  setLimit,
} = menuSlice.actions;

export default menuSlice.reducer;
